import { useMemo } from "react";
import { useFormikContext } from "formik";
import { ICheckout, Step } from "@/pages/checkout/hooks/useCheckoutSteps";
import DataCell from "@/components/DataCell";
import { creditCardOptions, currencyOptions, paymentMethodsLabels } from "@/constants/paymentMethods";
import SummaryPanel from "@/pages/checkout/components/ConfirmationView/components/SummaryPanel";
import intersection from "lodash/intersection";
import { formatPhone } from "@/utils/phones";
import { Bank, Cash, PaymentMethods, PaymentMethodsType } from "@/types/organizations";
// import useNeighborhood from "@/hooks/useNeighborhood";
import { Stack, Tooltip } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Call02Icon, Copy01Icon, CreditCardIcon, Location01Icon } from "@hugeicons/core-free-icons";
import useSnackbar from "@/hooks/useSnackbar";
import Price from "@/components/Price";

interface StepSummaryProps {
  step: Step;
}

const StepSummary = ({ step }: StepSummaryProps) => {
  const formik = useFormikContext<ICheckout>();
  // const { getNeighborhoodName } = useNeighborhood();
  const [openSnackbar] = useSnackbar();

  const paymentMethod = useMemo(() => {
    if (!formik.values.paymentDetails) return "";
    return paymentMethodsLabels[formik.values.paymentDetails.method as PaymentMethods] ?? "";
  }, [formik.values.paymentDetails]);

  const hasErrors = () => {
    const intfieldsWithErrors = intersection(Object.keys(formik.errors), Object.keys(step?.initialValues ?? {}));
    return !!intfieldsWithErrors.length;
  };

  switch (step.id) {
    case "contact":
      return (
        <SummaryPanel title="¿Quién recibirá el pedido?" icon={<HugeiconsIcon icon={Call02Icon} />} stepId={step.id} error={hasErrors() ? "Información incompleta" : undefined}>
          <DataCell label="Nombre" value={`${formik.values.name} ${formik.values.lastname}`} />
          <DataCell label="Teléfono" value={formatPhone(formik.values.phone, 505)} />
          {formik.values.email ? <DataCell label="Email" value={formik.values.email} /> : <></>}
        </SummaryPanel>
      );
    case "branch":
      return (
        <SummaryPanel title="Dirección de recogida de tu pedido" icon={<HugeiconsIcon icon={Location01Icon} />} stepId={step.id} error={hasErrors() ? "Información incompleta" : undefined}>
          <DataCell label="Sucursal" value={formik.values.branch?.title || ""} />
          <DataCell label="Dirección" value={formik.values.branch?.location?.address || ""} />
          <DataCell label="Teléfono" value={formatPhone(formik.values.branch?.whatsapp || "", 505)} />
        </SummaryPanel>
      );
    case "time":
      return (
        <SummaryPanel title="Hora de recogida de tu pedido" icon={<HugeiconsIcon icon={Location01Icon} />} stepId={step.id} error={hasErrors() ? "Información incompleta" : undefined}>
          <DataCell
            label="Horario"
            value={
              formik.values.scheduleTime
                ? new Date(formik.values.scheduleTime).toLocaleString("es-ES", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
                : ""
            }
          />
        </SummaryPanel>
      );

    case "location":
      return (
        <SummaryPanel title="¿Dónde deseas recibir tu pedido?" icon={<HugeiconsIcon icon={Location01Icon} />} stepId={step.id} error={hasErrors() ? "Información incompleta" : undefined}>
          {/* <DataCell label="Barrio" value={getNeighborhoodName(formik.values.neighborhood)} /> */}
          <DataCell label="Dirección" value={formik.values.address} />
        </SummaryPanel>
      );
    case "payment":
      return (
        <SummaryPanel title="¿Cómo deseas pagar?" icon={<HugeiconsIcon icon={CreditCardIcon} />} stepId={step.id} error={hasErrors() ? "Información incompleta" : undefined}>
          <DataCell label="Método de pago" value={paymentMethod} />
          {formik.values.paymentDetails?.method === PaymentMethodsType.CASH ? (
            <DataCell label="Paga con" value={<Price currency="NIO">{(formik.values?.paymentDetails?.value as Cash)?.amount}</Price>} />
          ) : (
            <></>
          )}
          {formik.values.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD && formik.values.paymentDetails?.value ? (
            <DataCell
              label="Tarjeta"
              value={
                creditCardOptions.find((cur) => cur.value === formik.values.paymentDetails?.value)?.label ||
                (typeof formik.values.paymentDetails?.value === "string" ? formik.values.paymentDetails?.value : undefined)
              }
            />
          ) : (
            <></>
          )}
          {formik.values.paymentDetails?.method === PaymentMethodsType.BANK_TRANSFER ? (
            <>
              <DataCell label="Banco" value={(formik.values.paymentDetails?.value as Bank)?.bankName || ""} />
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <DataCell
                  label="No. de Cuenta"
                  value={(formik.values.paymentDetails?.value as Bank)?.accountNumber || ""}
                  sx={{ display: "flex", flex: 1, alignItems: "center" }}
                />
                <Tooltip title="Copiar número de cuenta">
                  <HugeiconsIcon
                    icon={Copy01Icon}
                    size={22}
                    style={{ cursor: "pointer" }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText((formik.values.paymentDetails?.value as Bank)?.accountNumber);
                        openSnackbar("Número de cuenta copiado al portapapeles", "info");
                      } catch (err) {
                        console.error("Error al copiar: ", err);
                      }
                    }}
                  />
                </Tooltip>
              </Stack>

              <DataCell
                label="Moneda"
                value={
                  (formik.values.paymentDetails?.value as Bank)?.currency
                    ? currencyOptions.find((cur) => cur.value === (formik.values.paymentDetails?.value as Bank)?.currency)?.label ||
                    (formik.values.paymentDetails?.value as Bank)?.currency
                    : ""
                }
              />
            </>
          ) : (
            <></>
          )}
        </SummaryPanel>
      );
  }
};

export default StepSummary;
